import {TrustPurpose} from '@sollapay/enums';

import {defaultTrustType} from './default.trust-type';
import {getGeneralSaleTrustType} from './general-sale.trust-type';
import {optionsTrustType} from './options.trust-type';
import {sevenPercentTrustType} from './seven-percent.trust-type';
import {TrustTypeStrategyNotFoundError} from '../errors';

import type {TrustTypeStrategy} from './trust-type.strategy';
import type {GeneralSaleTransactionType} from '@sollapay/enums';

type TrustTypeResolver = (transactionType?: GeneralSaleTransactionType | null) => TrustTypeStrategy;

/**
 * Registry of trust type strategies, keyed by `TrustPurpose`.
 *
 * Adding a new trust type: implement `TrustTypeStrategy` in its own file and add one
 * entry here — the exhaustive `Record` makes a missing entry a compile error. Entries are
 * resolvers so a purpose may narrow further on a sub-discriminator.
 */
const trustTypeStrategies: Record<TrustPurpose, TrustTypeResolver> = {
  [TrustPurpose.SEVEN_PERCENT]: () => sevenPercentTrustType,
  [TrustPurpose.GENERAL_SALE]: getGeneralSaleTrustType,
  [TrustPurpose.OPTIONS]: () => optionsTrustType
};

/**
 * Gets the strategy for a trust purpose. `null` (no purpose set) resolves to the
 * conservative default strategy rather than being scattered as `?? default` at call sites.
 * `transactionType` only narrows general sale.
 *
 * @example
 * getTrustTypeStrategy(TrustPurpose.SEVEN_PERCENT).maxCollectionPercentage // => 7
 * getTrustTypeStrategy(null).maxCollectionPercentage // => 100 (default)
 */
export function getTrustTypeStrategy(
  purpose: TrustPurpose | null,
  transactionType?: GeneralSaleTransactionType | null
): TrustTypeStrategy {
  if (!purpose) {
    return defaultTrustType;
  }
  const resolve = trustTypeStrategies[purpose];
  if (!resolve) {
    throw new TrustTypeStrategyNotFoundError(purpose);
  }
  return resolve(transactionType);
}
